import Help from "@mui/icons-material/Help";
import { FormControl, FormHelperText, InputLabel, TextField, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { emptyValues } from "../../../app/js/utils/constants";
import ErrorIcon from "./ErrorIcon";

const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "22px",
    flex: "1 1 0",
    width: "100%",
}));

const StyledInputLabel = styled(InputLabel)(() => ({
    fontSize: "13px",
    color: "rgba(0, 0, 0, 0.6)",
    textTransform: "uppercase",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    width: "100%",
    "& .MuiInputBase-root": {
        backgroundColor: theme.palette.common.white,
        fontSize: 14,
        minWidth: "40px",
        marginTop: "var(--bsselect-input-mt, 22px)",
        transition: theme.transitions.create(["border-color", "box-shadow"]),
        border: "none",
    },
    "& .MuiNativeSelect-select": {
        fontSize: "14px",
        padding: "9px 6px",
        boxSizing: "border-box",
        height: "39px",
        width: "100%",
        "&:focus": {
            backgroundColor: "transparent",
        },
    },
    "&.combinedFieldShiftLeft": {
        marginLeft: "-2px",
        borderRadius: "0 4px 4px 0",
        width: "100%",
    },
    "&.labelLessShiftLeft": {
        margin: "2px 0 0 -2px",
        "& div": {
            borderRadius: "0 4px 4px 0",
        },
    },
    "& .errorBorder": {
        borderColor: "red",
        borderWidth: "1px",
    },
}));

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    }),
}));

const StyledBSSelectTextFieldDiv = styled("div")({
    display: "flex",
    flexDirection: "row",
    gap: "1vw",
    flex: "1 1 0",
});

const StyledBSSelectTextFieldFlexDiv = styled("div")({
    flex: "1 1 0",
});

const BSSelect = (props) => {
    const {
        disabled,
        error,
        errorText,
        extraInputStyles,
        extraFormControlStyles,
        helperText,
        helperTextRequired,
        hideErrorIcon,
        id,
        label,
        noFullWidth,
        onBlurValidate,
        onChange,
        onClick,
        options,
        placeholder,
        toolTipText,
        value,
        combinedField,
    } = props;

    const accepted_values = new Set(options.map((option) => String(option["value"])));
    const isEmpty = (val) => emptyValues.has(val);

    const [localError, setLocalError] = useState(false);

    useEffect(() => {
        if (localError) setLocalError(false);
    }, [value]);

    const handleOnBlur = () => {
        if (onBlurValidate) {
            setLocalError(!!onBlurValidate(id, value));
        }
    };

    const hasError = error || localError;

    const showHelperText = () => {
        if (helperText && !hasError) {
            if (isEmpty(value)) {
                return helperText;
            }
        }
        return;
    };

    const helperTextContext = showHelperText();

    return (
        <StyledFormControl fullWidth={!noFullWidth} style={extraFormControlStyles}>
            {label ? (
                <StyledInputLabel shrink htmlFor={id}>
                    {label}
                    {toolTipText && (
                        <Tooltip title={toolTipText} placement="right-start">
                            <span data-tooltip-id={`${id}-tool-tip`}>
                                <Help style={{ fontSize: "110%" }} />
                            </span>
                        </Tooltip>
                    )}
                    {hasError && <ErrorIcon />}
                </StyledInputLabel>
            ) : (
                hasError &&
                !hideErrorIcon && (
                    <StyledInputLabel shrink htmlFor={id}>
                        {<ErrorIcon />}
                    </StyledInputLabel>
                )
            )}
            <StyledBSSelectTextFieldDiv>
                <StyledBSSelectTextFieldFlexDiv>
                    <StyledTextField
                        select
                        id={id}
                        value={value}
                        disabled={disabled}
                        onChange={onChange}
                        onClick={onClick}
                        onBlur={handleOnBlur}
                        error={hasError}
                        placeholder={placeholder}
                        style={extraInputStyles}
                        className={
                            combinedField === "shiftLeft"
                                ? "combinedFieldShiftLeft"
                                : combinedField === "labelLessShiftLeft"
                                  ? "labelLessShiftLeft"
                                  : ""
                        }
                        defaultValue=""
                        slotProps={{
                            input: {
                                disableUnderline: true,
                            },
                            select: {
                                native: true,
                            },
                        }}
                    >
                        {accepted_values.has(String(value)) === false && value && (
                            <option key={value} value={value} disabled={true}>
                                {value}
                            </option>
                        )}
                        {options.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                                hidden={option.hidden}
                            >
                                {option.label}
                            </option>
                        ))}
                    </StyledTextField>
                </StyledBSSelectTextFieldFlexDiv>
            </StyledBSSelectTextFieldDiv>
            {errorText && error ? (
                <StyledFormHelperText error={true}>{errorText}</StyledFormHelperText>
            ) : (
                helperTextContext && (
                    <StyledFormHelperText required={helperTextRequired}>{helperText}</StyledFormHelperText>
                )
            )}
        </StyledFormControl>
    );
};

BSSelect.propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    extraInputStyles: PropTypes.object,
    extraFormControlStyles: PropTypes.object,
    helperText: PropTypes.string,
    helperTextRequired: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    noFullWidth: PropTypes.bool,
    onBlurValidate: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            disabled: PropTypes.bool,
        })
    ).isRequired,
    placeholder: PropTypes.string,
    toolTipText: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    combinedField: PropTypes.oneOf(["shiftLeft", "labelLessShiftLeft"]),
};

export default BSSelect;
